import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as S from './styles';
import { FormInputProps } from './types';

const FormInput: React.FC<FormInputProps> = ({
  label,
  required = false,
  error,
  inputSize = 'medium',
  labelPosition = 'left',
  width,
  leftIcon,
  rightIcon,
  helperText,
  showCharCount,
  maxLength,
  containerClassName,
  fullWidth = false,
  disabled,
  value,
  onChange,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    },
    [props]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setTouched(true);
      props.onBlur?.(e);
    },
    [props]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // Se maxLength estiver definido, limita o tamanho do texto
      if (maxLength !== undefined && newValue.length > maxLength) {
        return;
      }

      setInputValue(newValue);

      if (onChange) {
        onChange(e);
      }
    },
    [maxLength, onChange]
  );

  // Função para focar no input quando a label for clicada (útil para posição floating)
  const handleLabelClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const hasError = Boolean(error);
  const hasValue = Boolean(inputValue);
  const isEmpty = !hasValue;
  const showRequiredError = required && isEmpty && touched;
  const errorMessage = error || (showRequiredError ? 'Este campo é obrigatório' : '');
  const isErrorVisible = hasError || showRequiredError;

  return (
    <S.FormInputContainer
      labelPosition={labelPosition}
      fullWidth={fullWidth}
      width={width}
      className={containerClassName}
    >
      {label && labelPosition !== 'inside' && labelPosition !== 'floating' && (
        <S.InputLabel
          onClick={handleLabelClick}
          htmlFor={props.id}
          required={required}
          labelPosition={labelPosition}
          hasError={hasError || showRequiredError}
        >
          {label}
        </S.InputLabel>
      )}

      <S.InputContainer
        inputSize={inputSize}
        hasError={hasError || showRequiredError}
      >
        {leftIcon && (
          <S.IconWrapper position="left">
            {leftIcon}
          </S.IconWrapper>
        )}

        {(labelPosition === 'floating' || labelPosition === 'inside') && label && (
          <S.InputLabel
            onClick={handleLabelClick}
            htmlFor={props.id}
            required={required}
            labelPosition={labelPosition}
            hasError={hasError || showRequiredError}
            isFocused={isFocused}
            hasValue={hasValue}
          >
            {label}
          </S.InputLabel>
        )}

        <S.Input
          ref={inputRef}
          {...props}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          hasLeftIcon={Boolean(leftIcon)}
          hasRightIcon={Boolean(rightIcon)}
          inputSize={inputSize}
          hasError={hasError || showRequiredError}
          isFocused={isFocused}
          disabled={disabled}
          maxLength={maxLength}
          aria-invalid={hasError || showRequiredError}
          aria-required={required}
        />

        {rightIcon && (
          <S.IconWrapper position="right">
            {rightIcon}
          </S.IconWrapper>
        )}
      <S.ErrorMessage visible={isErrorVisible}>
        {errorMessage}
      </S.ErrorMessage>
      </S.InputContainer>


      {helperText && !hasError && !showRequiredError && (
        <S.HelperText>{helperText}</S.HelperText>
      )}

      {showCharCount && maxLength && (
        <S.CharCount>
          {inputValue.length}/{maxLength}
        </S.CharCount>
      )}
    </S.FormInputContainer>
  );
};

export default FormInput;
